import type { IEntityContentJson } from "@beefree.io/sdk/dist/types/bee";
import type { BadgeProps } from "@hightouch/htui";
import { captureException } from "src/lib/monitoring";
import type {
  CepDynamicContentUsageFragment,
  CepWhatsAppTemplatePreviewFragment,
  GetMessageByIdQuery,
  GetMessagesQuery,
} from "src/graphql";
import { DerivedCampaignStatus } from "src/graphql";
import {
  DerivedStatusToBadgeProps,
  DerivedStatusToIconMap,
  DerivedStatusToLabelMap,
} from "src/cep/campaigns/types";
import {
  parseRcsContent,
  type RcsContent,
} from "@hightouch/lib/cep/types/rcs-content";
import type { CepInappPayload } from "@hightouch/lib/cep/inapp-messages/schema/model";
import { EditorType } from "@hightouch/lib/cep/types/editor-type";
import type { agentEditorState } from "@hightouch/lib/cep/types/editor-state";
import { readWhatsAppEditorContent } from "@hightouch/lib/cep/types/whatsapp-template";
import type { WhatsAppTemplateContent } from "@hightouch/lib/cep/types/whatsapp-template-content";
import type { ProfileMapping } from "src/cep/messages/extract-profile-mappings";
import type { MessageDynamicContent } from "src/cep/messages/message/message-composer/dynamic-content/saved-groups";
import {
  DEFAULT_SEND_LIMITS,
  DEFAULT_SEND_RATE,
  DEFAULT_VOLUME_CAP,
  type MessageSendLimits,
} from "@hightouch/lib/cep/types/config-types";
import {
  ChannelType,
  type MessageAttributionMethod,
  type CepSenderConfig,
  type MessageTargetingConfig,
} from "src/types/cep";
import {
  DEFAULT_PUSH_ADVANCED_CONFIG,
  DEFAULT_PUSH_EDITOR_CONTENT,
  type ActionButtonEntry,
  type CustomDataEntry,
  type PushAdvancedConfig,
  type PushClickBehavior,
  type PushEditorContent,
  type PushImage,
  type PushRenderedOutput,
  type SmsEditorContent,
  type SmsRenderedOutput,
} from "@hightouch/lib/cep/types/sms-push-content";
import type { MessageNodeConfig } from "src/types/journeys";
import type { JourneyStatus } from "src/types/journeys";
import { getJourneyState } from "src/pages/journeys/utils/journey-state";
import type { EventCondition } from "src/types/visual";

import type {
  MergeTagColumn as GraphqlMergeTagColumn,
  MakeOptional,
} from "src/graphql";

export {
  DEFAULT_PUSH_ADVANCED_CONFIG,
  DEFAULT_PUSH_EDITOR_CONTENT,
  type ActionButtonEntry,
  type CustomDataEntry,
  type PushAdvancedConfig,
  type PushClickBehavior,
  type PushEditorContent,
  type PushImage,
  type PushRenderedOutput,
  type SmsEditorContent,
  type SmsRenderedOutput,
};

// Optional because columns built in the app (Message context, fixtures) and queries that don't select it leave it out.
export type MergeTagColumn = MakeOptional<
  GraphqlMergeTagColumn,
  "is_realtime_trait"
> & { is_realtime_entry_event?: true };

export enum EngagementMetric {
  Sent = "sent",
  Delivered = "delivered",
  Opened = "opened",
  Clicked = "clicked",
}

const CHANNEL_METRICS: Record<ChannelType, EngagementMetric[]> = {
  email: [
    EngagementMetric.Sent,
    EngagementMetric.Delivered,
    EngagementMetric.Opened,
    EngagementMetric.Clicked,
  ],
  sms: [
    EngagementMetric.Sent,
    EngagementMetric.Delivered,
    EngagementMetric.Clicked,
  ],
  whatsapp: [
    EngagementMetric.Sent,
    EngagementMetric.Delivered,
    EngagementMetric.Opened,
    EngagementMetric.Clicked,
  ],
  push: [EngagementMetric.Sent, EngagementMetric.Opened],
  // Per-message stats never count in-app clicks, so only the profile explorer shows them.
  in_app: [],
};

export function getChannelFromNode(node: {
  config: MessageNodeConfig;
}): ChannelType {
  return node.config.message_type;
}

function toChannelList(
  channels: ChannelType | ChannelType[] | string | null | undefined,
): string[] {
  if (!channels) return [];
  return Array.isArray(channels) ? channels : [channels];
}

function isKnownChannel(channel: string): channel is ChannelType {
  return Object.hasOwn(CHANNEL_METRICS, channel);
}

type OpenedMetricCopy = {
  label: string;
  count: string;
  rate: string;
  rateTitle: string;
  /** Counted from read receipts, which recipients can turn off. */
  fromReadReceipts: boolean;
};

/** Channels whose opened metric comes from read receipts, so it reads as Read. */
const READ_RECEIPT_CHANNELS: ReadonlySet<string> = new Set([
  ChannelType.Sms,
  ChannelType.Whatsapp,
]);

export function openedMetricCopy(
  channels: ChannelType | ChannelType[] | string | null | undefined,
): OpenedMetricCopy {
  const arr = toChannelList(channels);
  return arr.length > 0 && arr.every((ch) => READ_RECEIPT_CHANNELS.has(ch))
    ? {
        label: "Read",
        count: "reads",
        rate: "read rate",
        rateTitle: "Read rate",
        fromReadReceipts: true,
      }
    : {
        label: "Opened",
        count: "opens",
        rate: "open rate",
        rateTitle: "Open rate",
        fromReadReceipts: false,
      };
}

export function channelHasMetric(
  channels: ChannelType | ChannelType[] | string | null | undefined,
  metric: EngagementMetric,
): boolean {
  const arr = toChannelList(channels);
  if (arr.length === 0) return true;
  return arr.some(
    (ch) => isKnownChannel(ch) && CHANNEL_METRICS[ch].includes(metric),
  );
}

/**
 * The single known `ChannelType` a set describes, or `null` when it is mixed,
 * empty, or wholly unknown. Metric icons are channel-specific, so a set that
 * isn't one clear channel (a journey's cross-channel "All messages" scope) has
 * no honest channel icon and callers fall back to a neutral one.
 */
export function getSoleChannel(
  channels: ChannelType | ChannelType[] | string | null | undefined,
): ChannelType | null {
  const known = [...new Set(toChannelList(channels).filter(isKnownChannel))];
  return known.length === 1 ? (known[0] ?? null) : null;
}

export { EditorType };

/** What each editor is called wherever one is named to a marketer. */
export const EDITOR_TYPE_LABELS: Record<EditorType, string> = {
  [EditorType.DragAndDrop]: "Drag & drop",
  [EditorType.Html]: "HTML",
  [EditorType.Agent]: "Agent",
  [EditorType.Sms]: "SMS",
  [EditorType.Push]: "Push",
  [EditorType.InApp]: "In-app",
  [EditorType.Whatsapp]: "WhatsApp",
};

export type InAppEditorContent = {
  templateId: string;
  templateVersion: number;
  values: CepInappPayload;
};

export type PushButtonActionType = "openApp" | "openUrl" | "customAction";

export type AvailablePushPlatforms = { ios: boolean; android: boolean };

export const ALL_PUSH_PLATFORMS: AvailablePushPlatforms = {
  ios: true,
  android: true,
};

export function deriveAvailablePushPlatforms(
  messagePushApps: ReadonlyArray<{
    push_app: { platform: string; enabled: boolean };
  }>,
): AvailablePushPlatforms {
  if (messagePushApps.length === 0) return ALL_PUSH_PLATFORMS;
  const platforms = new Set(
    messagePushApps
      .filter((mpa) => mpa.push_app.enabled)
      .map((mpa) => mpa.push_app.platform),
  );
  return { ios: platforms.has("ios"), android: platforms.has("android") };
}

export type PushPlatform = "both" | "ios" | "android";

/** DB-level action button with platform tag (for serialization only). */
export type PushActionButton = {
  id: string;
  platform: PushPlatform;
  name: string;
  url: string;
};

/**
 * Derived from the push_config fields returned by GetMessageByIdQuery.
 * Keep in sync with the push_config fragment in cep-campaigns.graphql.
 */
export type PushConfigData = NonNullable<
  NonNullable<GetMessageByIdQuery["messages_by_pk"]>["push_config"]
>;

export type EmailEditorType =
  | EditorType.DragAndDrop
  | EditorType.Html
  | EditorType.Agent;

// Email is the only channel that uses content blocks today, but the
// authoring-surface choice (DnD vs HTML) is independent of channel.
export type ContentBlockEditorType = EditorType.DragAndDrop | EditorType.Html;

/**
 * Raw message type from GraphQL query (JSONB fields are untyped)
 */
type RawMessage = NonNullable<GetMessageByIdQuery["messages_by_pk"]>;
type RawContent = NonNullable<RawMessage["current_content"]>;

type LinkedEmailVersion = {
  id: string;
  subject: string | null;
  preheader: string | null;
  resolved_html: { html: string | null } | null;
} | null;

/**
 * Discriminated union for the linked LCM email on a `message_contents` row.
 * `content_email_version` = pinned version on this row (null if unpinned).
 * `content_email.content_email_version` = email's current version (null while generating).
 */
export type LinkedContentRef =
  | {
      content_email_id: string;
      content_email_version: LinkedEmailVersion;
      content_email: {
        id: string;
        name: string;
        llm_chat_session_id: string | null;
        content_email_version: LinkedEmailVersion;
        content_dynamic_slot_usages: CepDynamicContentUsageFragment[];
      };
    }
  | {
      content_email_id: null;
      content_email_version: LinkedEmailVersion;
      content_email: null;
    };

/**
 * Columns that are NOT NULL on `message_contents` but arrive nullable because
 * the relationships read through `message_contents_base` — Postgres reports
 * every view column as nullable regardless of the source column. Restoring
 * them here keeps the nullability lie out of consuming components.
 */
type ContentNotNullColumns = "id" | "version";

/** Narrowed content row: discriminated `LinkedContentRef`, typed JSONB, restored NOT-NULL columns. */
type NarrowedContent<T extends NarrowableContent> = Omit<
  T,
  | "editor_state"
  | "rendered_output"
  | "content_email_id"
  | "content_email_version"
  | "content_email"
  | ContentNotNullColumns
> &
  LinkedContentRef & {
    editor_state: EditorState;
    rendered_output: RenderedOutput;
  } & {
    [K in ContentNotNullColumns]: NonNullable<T[K]>;
  };

type NarrowableContent = {
  content_email_id: string | null;
  content_email_version: LinkedEmailVersion;
  content_email: {
    id: string;
    name: string;
    llm_chat_session_id: string | null;
    content_email_version: LinkedEmailVersion;
    content_dynamic_slot_usages: CepDynamicContentUsageFragment[];
  } | null;
  id: string | null;
  version: number | null;
  editor_state: unknown;
  rendered_output: unknown;
};

/**
 * Narrows a raw GraphQL content row at the loader boundary: corrects the
 * `message_contents_base` nullability lie (`id`/`version` restored to non-null)
 * and casts untyped JSONB fields. FK integrity is server-enforced — a non-null
 * `content_email_id` always has a matching `content_email` row.
 */
export function narrowLinkedContentRef<T extends NarrowableContent>(
  content: T,
): NarrowedContent<T> {
  const {
    content_email_id,
    content_email_version,
    content_email,
    id,
    version,
    editor_state,
    rendered_output,
    ...rest
  } = content;
  const typedColumns = {
    id: id as NonNullable<T["id"]>,
    version: version as NonNullable<T["version"]>,
    editor_state: editor_state as EditorState,
    rendered_output: rendered_output as RenderedOutput,
  };
  if (content_email_id !== null && content_email !== null) {
    return {
      ...rest,
      ...typedColumns,
      content_email_id,
      content_email_version,
      content_email,
    };
  }
  if (content_email_id !== null) {
    // FK-backed: impossible in practice; surface rather than silently degrade.
    captureException(
      new Error(
        `message content has content_email_id ${content_email_id} but no content_email row`,
      ),
    );
  }
  return {
    ...rest,
    ...typedColumns,
    content_email_id: null,
    content_email_version,
    content_email: null,
  };
}

export type TypedContent = NarrowedContent<RawContent>;

type RawSenderIdentity = NonNullable<RawMessage["cep_sender_identity"]>;

export type TypedSenderIdentity = Omit<RawSenderIdentity, "config"> & {
  config: CepSenderConfig | null;
};

/**
 * Message with properly typed JSONB fields (send_gates, measurement_config,
 * cep_sender_identity.config) and narrowed column types (channel_type).
 */
export type TypedMessage = Omit<
  RawMessage,
  | "send_config"
  | "send_gates"
  | "measurement_config"
  | "targeting_config"
  | "current_content"
  | "draft_content"
  | "channel_type"
  | "cep_sender_identity"
> & {
  channel_type: ChannelType | null;
  send_config: MessageSendConfig | null;
  send_gates: StoredSendGates | null;
  measurement_config: MeasurementConfig | null;
  targeting_config: MessageTargetingConfig | null;
  current_content: TypedContent | null;
  /** The staged (unpublished) content version, mapped from
   *  messages.draft_version. Null when there are no staged changes — see
   *  `useCurrentSurface().editableContent`. */
  draft_content: TypedContent | null;
  cep_sender_identity: TypedSenderIdentity | null;
};

export const MessageStatus = {
  Draft: "draft",
  Active: "active",
  Paused: "paused",
  Completed: "completed",
  Scheduled: "scheduled",
  Archived: "archived",
} as const;

export type MessageStatus = (typeof MessageStatus)[keyof typeof MessageStatus];

export type EditorState =
  | {
      type: "json";
      provider: "beefree";
      editorType: EditorType.DragAndDrop;
      editorState: IEntityContentJson;
    }
  | {
      type: "html";
      provider: "codemirror";
      editorType: EditorType.Html;
      editorState: string;
    }
  | ReturnType<typeof agentEditorState>
  | {
      editorType: EditorType.Sms;
      editorState: string | SmsEditorContent;
    }
  | {
      editorType: EditorType.Push;
      editorState: PushEditorContent;
    }
  | {
      editorType: EditorType.InApp;
      editorState: InAppEditorContent;
    }
  | {
      editorType: EditorType.Whatsapp;
      editorState: WhatsAppTemplateContent;
    }
  | null;

/**
 * Deliberately carries no payload. The values live in
 * `cep_inapp_message_contents`, and a `Record<string, unknown>` here breaks
 * every journeys form: react-hook-form's `DeepPartial` maps `unknown` to `{}`.
 */
export type InAppRenderedOutput = {
  templateId: string;
  templateVersion: number;
};

export type RenderedOutput =
  | { html: string }
  | SmsRenderedOutput
  | PushRenderedOutput
  | InAppRenderedOutput
  | null;

export function isEmailRenderedOutput(
  output: RenderedOutput | undefined,
): output is { html: string } {
  return output != null && "html" in output;
}

/** Empty string for a missing or non-email body, so callers can compare bodies
 *  across A/B arms without special-casing the ones not written yet. */
export function emailHtmlOf(output: RenderedOutput | null | undefined): string {
  const value = output ?? undefined;
  return isEmailRenderedOutput(value) ? value.html : "";
}

/**
 * Normalizes the SMS editor state which can be either a bare string (legacy)
 * or a structured object with body + optional link.
 */
export function parseSmsEditorState(state: string | SmsEditorContent): {
  body: string;
  link: string;
} {
  if (typeof state === "string") {
    return { body: state, link: "" };
  }
  // Deliberately omits `mediaUrl`: the result is spread into per-arm variant
  // drafts, and media is base-content-only. Use `getSmsEditorMediaUrl` instead.
  return { body: state.body, link: state.link ?? "" };
}

/**
 * The lane an SMS message was authored in, or null when it never chose one.
 */
export function getSmsEditorLane(
  editorState: EditorState | null | undefined,
): "rcs" | "fallback" | null {
  if (editorState?.editorType !== EditorType.Sms) return null;
  const state = editorState.editorState;
  if (typeof state === "string") return null;
  return state.lane ?? null;
}

/**
 * Extracts the rich notification image URL from a push editor state.
 * Returns null for non-push editor states and normalizes the empty string the
 * image field's URL mode saves while the input is blank.
 */
export function getPushEditorImageUrl(
  editorState: EditorState | null | undefined,
): string | null {
  return editorState?.editorType === EditorType.Push
    ? editorState.editorState.image?.url || null
    : null;
}

/**
 * Extracts the MMS media URL from an SMS editor state. Returns null for
 * non-SMS states, for the legacy bare-string state, and for the empty string
 * the media field saves while its URL input is blank.
 */
export function getSmsEditorMediaUrl(
  editorState: EditorState | null | undefined,
): string | null {
  if (editorState?.editorType !== EditorType.Sms) return null;
  const state = editorState.editorState;
  return typeof state === "string" ? null : state.mediaUrl || null;
}

/** Null for another editor's state; parsed rather than cast because `editor_state` is jsonb. */
export function getWhatsAppEditorContent(
  editorState: unknown,
): WhatsAppTemplateContent | null {
  if (
    typeof editorState !== "object" ||
    editorState === null ||
    !("editorType" in editorState) ||
    editorState.editorType !== EditorType.Whatsapp
  ) {
    return null;
  }
  return readWhatsAppEditorContent(
    "editorState" in editorState ? editorState.editorState : null,
  );
}

/** A WhatsApp message's previews read its template body through the SMS shape. */
export function whatsAppRenderedOutput(
  content: WhatsAppTemplateContent,
): SmsRenderedOutput {
  return { body: content.body };
}

/**
 * A WhatsApp message's content rows leave rendered_output empty; its template's
 * base locale holds the content.
 */
export function whatsAppTemplatePreviewOutput(
  message: CepWhatsAppTemplatePreviewFragment,
): SmsRenderedOutput | undefined {
  const locale =
    message.whatsapp_template_content[0]?.cep_whatsapp_template
      ?.cep_whatsapp_template_locales[0];
  return locale
    ? whatsAppRenderedOutput(readWhatsAppEditorContent(locale.editor_state))
    : undefined;
}

export function isSmsRenderedOutput(
  output: RenderedOutput | undefined,
): output is SmsRenderedOutput {
  return output != null && "body" in output && !("title" in output);
}

/**
 * The authored card on a content row, or null.
 *
 * Parsed rather than cast: `rendered_output` is jsonb, so a malformed lane has
 * to read as "no card" instead of crashing the editor.
 */
export function getRcsContent(
  output: RenderedOutput | null | undefined,
): RcsContent | null {
  return parseRcsContent(output ?? null);
}

export function isPushRenderedOutput(
  output: RenderedOutput | undefined,
): output is PushRenderedOutput {
  return output != null && "title" in output && "body" in output;
}

export function isInAppRenderedOutput(
  output: RenderedOutput | undefined,
): output is InAppRenderedOutput {
  return output != null && "templateId" in output;
}

export type OnSaveEditorData = {
  editorState: NonNullable<EditorState>;
  renderedOutput: NonNullable<RenderedOutput>;
  subject?: string;
  preheader?: string | null;
  profileMappings?: ProfileMapping[];
  /** The Dynamic content changeset that the drag-and-drop editor wrote markers for, saved in the same write. */
  dynamicContent?: MessageDynamicContent;
  /** Set by an in-app editor whose host persists its push app links, as a journey tile does, when the user changed them. */
  pushAppIds?: string[];
  /**
   * Which save toasts this call owns; defaults to `"both"`.
   * - `"error-only"`: the save wasn't asked for directly (auto-detach) or
   *   reports success another way (editor switch, template apply), but a
   *   failure still needs surfacing.
   * - `"none"`: the caller toasts for itself, e.g. a form. Failures still reach
   *   Sentry and still resolve `false`.
   * Toast-only — it does not affect publishing or the publish-changes gate.
   */
  toasts?: "both" | "error-only" | "none";
};

export type HtmlHandoff =
  /** The editor placed the Dynamic content markers. The changeset is saved with the HTML. */
  | { kind: "markers-placed"; dynamicContent?: MessageDynamicContent }
  /** The composer prepares the HTML as an HTML editor save. */
  | { kind: "html-save" };

export {
  DEFAULT_SEND_LIMITS,
  DEFAULT_SEND_RATE,
  type MessageSendLimits,
} from "@hightouch/lib/cep/types/config-types";

export type MessageContentMetadata = {
  subject: string;
  preheader?: string;
  editorState: EditorState;
  renderedOutput: RenderedOutput;
};

export type MessageSendConfig = {
  reply_to_name?: string | null;
  reply_to_email?: string | null;
};

export const DEFAULT_SEND_CONFIG: MessageSendConfig = {};

/**
 * `send_gates` as stored in jsonb: rows written before a gate axis shipped
 * lack that key, so every axis is optional. Normalize with
 * `sendGatesToSendLimits` before treating it as `MessageSendLimits`.
 */
export type StoredSendGates = Partial<MessageSendLimits>;

export const asStoredSendGates = (sendGates: unknown): StoredSendGates | null =>
  (sendGates as StoredSendGates | null) ?? null;

export const sendGatesToSendLimits = (
  sendGates: StoredSendGates | null | undefined,
): MessageSendLimits => ({
  quietHours: sendGates?.quietHours ?? DEFAULT_SEND_LIMITS.quietHours,
  frequencyCapping:
    sendGates?.frequencyCapping ?? DEFAULT_SEND_LIMITS.frequencyCapping,
  repeatSend: sendGates?.repeatSend ?? DEFAULT_SEND_LIMITS.repeatSend,
  volumeCap: sendGates?.volumeCap ?? DEFAULT_VOLUME_CAP,
  // Merge over defaults so pre-Duration rows (no mode/duration fields) normalize to a complete config.
  sendRate: sendGates?.sendRate
    ? { ...DEFAULT_SEND_RATE, ...sendGates.sendRate }
    : DEFAULT_SEND_RATE,
});

export type MeasurementConfig = {
  conversionEvent?: EventCondition;
  attributionMethod?: MessageAttributionMethod;
};

export type MessagePerformance = {
  enabled: boolean;
  conversionEvent: {
    event: EventCondition;
    atLeast: number;
  };
  attributionMethod: MessageAttributionMethod;
};

export type Message = {
  id: string;
  workspaceId: number;
  name: string;
  sendGates: MessageSendLimits;
  measurementConfig: MeasurementConfig;
  messageSubscriptionTopicId?: string;
  tags: Record<string, unknown>;
  createdAt: string;
  createdBy: number;
  updatedAt: string;
  updatedBy: number;
};

export type MessageContent = {
  id: string;
  workspaceId: number;
  messageId: string;
  version: number;
  subject: string;
  preheader?: string;
  editorState: IEntityContentJson;
  renderedOutput?: RenderedOutput;
  createdAt: string;
  createdBy: number;
};

// These are the display states returned by getJourneyState (not raw DB values).
// "draft" and "paused" are omitted here because they overlap with
// DerivedCampaignStatus and are already covered by DerivedStatusToLabelMap /
// DerivedStatusToBadgeProps.
type JourneyTriggerStatus =
  | "active"
  | "deleting"
  | "draining"
  | "staged"
  | "off";

export type TriggerStatus = JourneyTriggerStatus | DerivedCampaignStatus;

export const TRIGGER_STATUS_LABEL: Record<TriggerStatus, string> = {
  active: "Active",
  deleting: "Deletion in progress",
  draining: "Draining",
  staged: "Staged",
  off: "Off",
  ...DerivedStatusToLabelMap,
};

// Journey states reuse the campaign status icons (the closest campaign status
// for each), so a journey-message row's status reads as the same icon-circle
// treatment as a campaign row (CampaignStatusBadges) and matches the Figma. The
// campaign-derived statuses map to their own icons directly.
export const TriggerStatusToIconMap: Record<
  TriggerStatus,
  (typeof DerivedStatusToIconMap)[DerivedCampaignStatus]
> = {
  active: DerivedStatusToIconMap[DerivedCampaignStatus.Activated],
  off: DerivedStatusToIconMap[DerivedCampaignStatus.Paused],
  staged: DerivedStatusToIconMap[DerivedCampaignStatus.Draft],
  draining: DerivedStatusToIconMap[DerivedCampaignStatus.Running],
  deleting: DerivedStatusToIconMap[DerivedCampaignStatus.Archived],
  ...DerivedStatusToIconMap,
};

const TRIGGER_STATUS_BADGE_PROPS: Record<
  TriggerStatus,
  Pick<BadgeProps, "variant" | "isPending">
> = {
  active: { variant: "info" },
  deleting: { variant: "subtle", isPending: true },
  draining: { variant: "info" },
  staged: { variant: "info" },
  off: { variant: "subtle" },
  ...DerivedStatusToBadgeProps,
};

export function isTriggerStatus(status: string): status is TriggerStatus {
  return status in TRIGGER_STATUS_BADGE_PROPS;
}

export function getTriggerBadgeProps(
  status: string,
): Pick<BadgeProps, "variant" | "isPending"> {
  return isTriggerStatus(status)
    ? TRIGGER_STATUS_BADGE_PROPS[status]
    : { variant: "subtle" };
}

type GeneratedMessageRow = GetMessagesQuery["messages"][number];

export type TriggerMessageInput = {
  campaign?: Pick<
    NonNullable<GeneratedMessageRow["campaign"]>,
    "id" | "name" | "status"
  > | null;
  journey_messages?: Array<{
    journey_node_id: string;
    journey_node: {
      journey_version: {
        id: string;
        name: string;
        status: string;
        deleted: boolean | null;
        runs_aggregate?: { aggregate: { count: number } | null } | null;
        nodes_aggregate?: { aggregate: { count: number } | null } | null;
      } | null;
    } | null;
  }>;
};

export type TriggerInfo =
  | {
      type: "campaign";
      id: string;
      name: string;
      status: string;
      href: string;
    }
  | {
      type: "journey";
      id: string;
      name: string;
      status: string;
      href: string;
    };

export function getTriggerInfo(
  message: TriggerMessageInput,
): TriggerInfo | null {
  const campaign = message.campaign;
  if (campaign) {
    return {
      type: "campaign",
      name: campaign.name ?? "Untitled campaign",
      id: campaign.id,
      status: campaign.status,
      href: `/native-delivery/campaigns/${campaign.id}`,
    };
  }

  const journeyVersion =
    message.journey_messages?.[0]?.journey_node?.journey_version;
  if (journeyVersion) {
    return {
      type: "journey",
      name: journeyVersion.name,
      id: journeyVersion.id,
      status: getJourneyState({
        deleted: journeyVersion.deleted,
        status: journeyVersion.status as JourneyStatus,
        numberOfUsersInJourney:
          journeyVersion.nodes_aggregate?.aggregate?.count ?? 0,
        numberOfPreviousRuns:
          journeyVersion.runs_aggregate?.aggregate?.count ?? 0,
      }),
      href: `/journeys/${journeyVersion.id}`,
    };
  }

  return null;
}
